import { formatDistanceToNow, parseISO } from "date-fns";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import type { FieldAttribute } from "../../../../../api/types/objectTypes";
import type {
  CitationReference,
  WorkflowActivity,
  WorkflowActivityChangeSet,
  WorkflowActivityChangeSetField,
  WorkflowActivityChangeSetSection,
  WorkflowDetails,
  WorkflowFileInfo,
} from "../../../../../api/types/workflowTypes";

export type WorkflowStep = { type: "activity"; activity: WorkflowActivity } | { type: "summary" };

export const getWorkflowStepTitle = (step: WorkflowStep): string => {
  if (step.type === "summary") {
    return "Summary";
  }

  return step.activity.name || autoFormatCamelCase(step.activity.type);
};

export const getCurrentWorkflowStepIndex = (data: WorkflowDetails): number => {
  const activities = data.workflow.activities;
  const hasSummary = data.workflow.progress.entries.length > 0;

  if (data.workflow.status === "InProgress") {
    return data.workflow.currentActivityIndex ?? 0;
  }

  if (hasSummary) {
    return activities.length;
  }

  return data.workflow.currentActivityIndex ?? Math.max(0, activities.length - 1);
};

export type ActivityDisplayStatus =
  "NotStarted" | "Executing" | "PendingApproval" | "Pending" | "Completed" | "Skipped" | "Cancelled";

export interface SignalDisabledReasons {
  unsavedInlineEdits?: string;
  missingObjectSelection?: string;
  missingRequiredFields?: string;
}

export const getSignalDisabledReason = (
  signal: WorkflowSignalType,
  reasons: SignalDisabledReasons
): string | undefined =>
  reasons.unsavedInlineEdits ??
  (signal === "Approve" ? (reasons.missingObjectSelection ?? reasons.missingRequiredFields) : undefined);

export const getMainChangeSet = (activity: WorkflowActivity): WorkflowActivityChangeSet | undefined =>
  activity.state === "None" || activity.state === "WaitingForSignal"
    ? activity.previewChangeSet
    : activity.appliedChangeSet;

export const getActivityDisplayStatus = (activity: WorkflowActivity): ActivityDisplayStatus => {
  if (activity.executionPhase === "Initializing" || activity.executionPhase === "Executing") {
    return "Executing";
  }

  switch (activity.state) {
    case "None":
      return "NotStarted";
    case "WaitingForSignal":
      return activity.executionPhase === "WaitingForUserSignal" ? "PendingApproval" : "Pending";
    case "Completed":
      return activity.completionKind === "Skipped" ? "Skipped" : "Completed";
    case "Cancelled":
      return "Cancelled";
  }
};

export const getStepTimeLabel = (activity: WorkflowActivity): string => {
  const status = getActivityDisplayStatus(activity);

  switch (status) {
    case "Executing":
      return "Executing...";
    case "NotStarted":
      return "Not started";
    case "PendingApproval":
    case "Pending":
      return activity.startedAt ? `Pending ${formatDistanceToNow(parseISO(activity.startedAt))}` : "Pending";
    case "Skipped":
      return "Skipped";
    case "Completed":
    case "Cancelled":
      return convertISODateTime(activity.finishedAt ?? activity.startedAt);
    default:
      return "";
  }
};

export const getDetailsTimeLabel = (activity: WorkflowActivity): string => {
  const status = getActivityDisplayStatus(activity);

  switch (status) {
    case "Executing":
      return activity.startedAt ? `Started at: ${convertISODateTime(activity.startedAt)}` : "";
    case "NotStarted":
      return "";
    case "PendingApproval":
    case "Pending":
      return activity.startedAt ? `Pending ${formatDistanceToNow(parseISO(activity.startedAt))}` : "Pending";
    case "Skipped":
      return "";
    case "Completed":
    case "Cancelled":
      return activity.finishedAt
        ? `Finished at: ${convertISODateTime(activity.finishedAt)}`
        : activity.startedAt
          ? `Started at: ${convertISODateTime(activity.startedAt)}`
          : "";
    default:
      return "";
  }
};

export type FilePreviewState =
  | { type: "document"; attachment: WorkflowFileInfo; selectedCitationId?: string }
  | { type: "citations"; activityId: string; documentName: string; fieldId?: string; selectedCitationId?: string }
  | { type: "email"; subject?: string };

export interface OpenFieldCitation {
  activityId: string;
  fieldId: string;
}

const isCitationLocatable = (citation: CitationReference): boolean =>
  !!citation.id && citation.highlightResolution?.status === "Resolved";

export const getFieldCitationKeys = (citations: CitationReference[] | undefined): Set<string> => {
  const keys = new Set<string>();
  for (const citation of citations ?? []) {
    if (citation.fieldId && isCitationLocatable(citation)) {
      keys.add(citation.fieldId);
    }
  }
  return keys;
};

export const findFieldCitation = (
  citations: CitationReference[] | undefined,
  fieldId: string
): CitationReference | undefined => citations?.find((c) => c.fieldId === fieldId && isCitationLocatable(c));

export const isVisibleField = (field: { attributes?: FieldAttribute[] }): boolean =>
  !field.attributes?.includes("HiddenByDefault");

export interface ChangeSetDisplayGroups {
  ungroupedFields: WorkflowActivityChangeSetField[];
  sectionGroups: { name: string; fields: WorkflowActivityChangeSetField[] }[];
}

// Splits a change set's fields into the ungrouped list and its section groups, filtering HiddenByDefault
// fields once up front. Sections are used in two ways:
// - Extraction grouping: sections reference fields that also live in the flat `fields` list. The flat
//   list is the single source of truth for values (edits update it; the section-embedded copies are
//   not refreshed), so a field found in `fields` is preferred over the section's embedded copy.
// - Fund Ops sections (wire instructions): sections carry fields that exist ONLY in the section — and
//   may reuse the same field name across sections with different values — so when a section field is
//   absent from the flat list, its embedded copy is the authoritative value (visible fields only —
//   HiddenByDefault fields are omitted from display either way).
export const getChangeSetDisplayGroups = (
  fields: WorkflowActivityChangeSetField[],
  sections: WorkflowActivityChangeSetSection[] | undefined
): ChangeSetDisplayGroups => {
  const visibleFields = fields.filter(isVisibleField);
  if (!sections || sections.length === 0) {
    return { ungroupedFields: visibleFields, sectionGroups: [] };
  }

  const sectionedNames = new Set(sections.flatMap((section) => section.fields.map((f) => f.name)));
  // Map over ALL flat fields so a hidden flat field still shadows a same-named embedded copy (and is then dropped).
  const fieldsByName = new Map(fields.map((field) => [field.name, field]));
  const sectionGroups = sections
    .map((section) => ({
      name: section.name,
      fields: section.fields
        .map((sectionField) => fieldsByName.get(sectionField.name) ?? sectionField)
        .filter(isVisibleField),
    }))
    .filter((group) => group.fields.length > 0);

  return { ungroupedFields: visibleFields.filter((f) => !sectionedNames.has(f.name)), sectionGroups };
};

export interface WorkflowRunHeaderActions {
  showRetry: boolean;
  showRestart: boolean;
  canRestart: boolean;
  showCancel: boolean;
  canCancel: boolean;
}

export const getWorkflowRunHeaderActions = ({
  workflow,
  canCurrentUserTriggerActions,
}: WorkflowDetails): WorkflowRunHeaderActions => {
  const isInProgress = canCurrentUserTriggerActions && workflow.status === "InProgress";
  const isFailed = canCurrentUserTriggerActions && workflow.status === "Failed";

  return {
    showRetry: isFailed,
    showRestart: isInProgress || isFailed,
    canRestart: isFailed || (isInProgress && workflow.requiresUserAction),
    showCancel: isInProgress,
    canCancel: isInProgress && workflow.requiresUserAction,
  };
};
